import React, { createContext, useContext, useEffect, useState, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useKeywordSearchSWR } from '../../hooks/useKeywordSearchSWR';
import KeywordTrackingHeader from '../../components/KeywordTrackingNewUIPage/KeywordTrackingHeader';
import OptimizedKeywordFilterTabs from '../../components/KeywordTrackingNewUIPage/OptimizedKeywordFilterTabs';
import KeywordinteligenceCards from '../../components/KeywordTrackingNewUIPage/KeywordinteligenceCards';
import KeywordSearchResults from '../../components/KeywordTrackingNewUIPage/KeywordSearchResults';
import { apiService } from '../../utils/prefetchManager';
import AppsTrackingPlanModal from '../../components/Common/Modal/AppsTrackingPlanModal';
import queryClient, { queryKeys } from '../../utils/queryClient';
import { usePerformanceMonitor } from '../../hooks/usePerformanceMonitor';
import { mutate as swrMutate } from 'swr';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { usePlatforms } from '../../contexts/PlatformsContext';
import { useAnalytics, ANALYTICS_EVENTS } from '../../analytics';

const OptimizedKeywordTrackingContext = createContext();

export const useOptimizedKeywordTracking = () => {
  const context = useContext(OptimizedKeywordTrackingContext);
  if (!context) throw new Error('useOptimizedKeywordTracking must be used within an OptimizedKeywordTrackingProvider');
  return context;
};

export const OptimizedKeywordTrackingProvider = ({ children }) => {
  const { trackEvent } = useAnalytics();
  const { measureOperation } = usePerformanceMonitor('OptimizedKeywordTracking');
  const { onboardingState, isLoading: onboardingLoading, markPageCompleted } = useOnboarding();
  const { getActivePlatforms, loading: platformsLoading } = usePlatforms();
  const location = useLocation();
  const processedUrlSearchRef = useRef(null);

  // State management
  // Default to first active platform from API
  const [activeTab, setActiveTab] = useState(() => {
    const activePlatforms = getActivePlatforms()
    return activePlatforms.length > 0 ? activePlatforms[0].id : 'meta'
  });
  const [activeChip, setActiveChip] = useState(-1);
  const [selectedChip, setSelectedChip] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  // The keyword that was actually submitted (used to drive RQ/SWR).
  // This is intentionally separated from the input `searchValue` so typing/backspacing
  // in the input does not automatically trigger network calls until the user submits.
  const [submittedKeyword, setSubmittedKeyword] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchFilters, setSearchFilters] = useState({});
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [hasSubmittedSearch, setHasSubmittedSearch] = useState(false);
  const [showKeywordOnboarding, setShowKeywordOnboarding] = useState(false);
  const [keywordOnboardingStep, setKeywordOnboardingStep] = useState(0); // 0: search, 1: filters, 2: chips, 3: profile

  // Utility functions
  const isUpgradeError = (error) => {
    const response = error?.response;
    const data = response?.data || {};
    const status = data?.status;
    const action = data?.action || '';
    const message = data?.message || '';
    return status === 400 && (action === 'UPGRADE' || /max limit/i.test(message));
  };

  const isUpgradeResponse = (data) => {
    if (!data || typeof data !== 'object') return false;
    const status = data?.status;
    const action = data?.action || '';
    const message = data?.message || '';
    return status === 400 && (action === 'UPGRADE' || /max limit/i.test(message));
  };

  /**
   * Keyword Explorer onboarding:
   * - Show tutorial if keyword_explorer flag is not true in onboarding_state
   * - Check onboardingState from centralized OnboardingContext
   * - A local sessionStorage flag ensures we don't re-open the tutorial repeatedly in the same session.
   */
  useEffect(() => {
    // Wait for the onboarding status to actually load before deciding whether to show
    // the tutorial. Otherwise, on refresh, this runs once with a still-empty
    // onboardingState (before the async/cached fetch resolves), incorrectly shows the
    // tutorial for users who already completed it, and never gets a chance to correct
    // course once the real "completed" state arrives.
    if (onboardingLoading) {
      return;
    }

    // If user has already seen tutorial in this browser session, skip.
    const seenKey = 'keywordExplorerOnboardingSeen';
    const sessionSeen = sessionStorage.getItem(seenKey) === 'true';
    if (sessionSeen) {
      return;
    }

    // Check if keyword_explorer onboarding is needed
    // Show tutorial if the flag is false, undefined, or null
    const keywordExplorerCompleted = onboardingState?.keyword_explorer === true;

    if (!keywordExplorerCompleted) {
      // Show tutorial for this page
      setShowKeywordOnboarding(true);
      setKeywordOnboardingStep(0);
      sessionStorage.setItem('keywordTutorialCurrentStep', '0');
      window.dispatchEvent(new CustomEvent('keywordTutorialStep', { detail: { step: 0 } }));
      // Don't mark as completed here - only mark when user clicks "Done" or "Skip All"
    } else {
      // Real state confirms this page is already completed - make sure the tutorial
      // isn't left showing from a stale pre-load render.
      setShowKeywordOnboarding(false);
    }
  }, [onboardingState, onboardingLoading, markPageCompleted]);

  /**
   * Keep keywordOnboardingStep in sync with cross-page tutorial events.
   * This lets the Profile tutorial (in DesktopHeader) drive the step back
   * to the chips section when the user clicks "Prev".
   */
  useEffect(() => {
    const handleKeywordTutorialStep = (e) => {
      if (!e || typeof e.detail?.step === 'undefined') return;
      const step = e.detail.step;

      // step >= 0 means a specific tutorial step is active
      if (typeof step === 'number' && step >= 0) {
        setShowKeywordOnboarding(true);
        setKeywordOnboardingStep(step);
        try {
          sessionStorage.setItem('keywordTutorialCurrentStep', String(step));
        } catch (err) {
          // ignore storage errors
        }
        return;
      }

      // step < 0 means tutorial is finished / cancelled
      if (typeof step === 'number' && step < 0) {
        setShowKeywordOnboarding(false);
        setKeywordOnboardingStep(0);
        try {
          sessionStorage.removeItem('keywordTutorialCurrentStep');
        } catch (err) {
          // ignore storage errors
        }
      }
    };

    window.addEventListener('keywordTutorialStep', handleKeywordTutorialStep);
    return () => window.removeEventListener('keywordTutorialStep', handleKeywordTutorialStep);
  }, []);

  // React Query for predefined keywords (cached, no refetch on tab change)
  const {
    data: keywordsResponse,
    isLoading: keywordsLoading,
    error: keywordsError,
    refetch: refetchKeywords
  } = useQuery({
    queryKey: queryKeys.keywords.predefined(),
    queryFn: async () => {
      return measureOperation('fetchPredefinedKeywords', async () => {
        const response = await apiService.fetchPredefinedKeywords();
        if (isUpgradeResponse(response)) {
          setShowUpgradeModal(true);
          return { keywords: [] };
        }
        return response;
      });
    },
    enabled: false, // Disable automatic fetching - only fetch when explicitly requested
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
    retry: (failureCount, error) => {
      if (isUpgradeError(error)) {
        setShowUpgradeModal(true);
        return false;
      }
      return failureCount < 2;
    },
  });

  // Process keywords into filter chips
  const filterChips = useMemo(() => {
    const keywords = Array.isArray(keywordsResponse?.keywords) ? keywordsResponse.keywords : [];
    return keywords.map((item, index) => ({
      label: item,
      active: false,
      id: index,
    }));
  }, [keywordsResponse]);

  // React Query for keyword search results (cached per keyword + platform + page)
  const currentKeyword = selectedChip || filterChips.find(chip => chip.active)?.label;
  // Platform follows the active tab (no 'all' option). activeTab will be
  // one of the active platform ids (e.g. 'meta', 'steam', 'apple_vision').
  const platform = activeTab || '';

  // Determine the effective keyword for the query. Use `submittedKeyword` as the
  // active search term so typing in the input (`searchValue`) doesn't trigger
  // API calls until the user explicitly submits the search.
  const effectiveKeyword = isSearchMode ? submittedKeyword : currentKeyword;

  // Use SWR polling for both manual searches and commonly searched keywords (chips)
  // This ensures polling works for new platforms that may take time to process data
  // Only use SWR when we have an effective keyword (either from search or chip selection)
  const shouldUseSWR = !!effectiveKeyword && effectiveKeyword.trim() !== '';


  const swr = useKeywordSearchSWR({
    keyword: shouldUseSWR && effectiveKeyword && effectiveKeyword.trim() !== '' ? effectiveKeyword : null,
    page: currentPage,
    platform,
    filters: searchFilters,
    intervalMs: 5000, // 5s; backend returns action=LOADING then we keep polling
    legacy: true, // Keyword Explorer keeps the old /tracker/keyword/{keyword} endpoint
  })

  // Open upgrade modal when SWR (search or chip selection) returns an upgrade response
  // Only open modal if we have a valid effective keyword (from search or chip selection)
  // Add additional check to prevent modal opening during typing/backspacing
  // Only open modal if user has actually submitted a search or selected a chip
  useEffect(() => {
    if (shouldUseSWR &&
      swr?.data &&
      isUpgradeResponse(swr.data) &&
      effectiveKeyword &&
      effectiveKeyword.trim() !== '') {
      setShowUpgradeModal(true)
    }
  }, [shouldUseSWR, swr?.data, effectiveKeyword])

  const {
    data: rqData,
    isLoading: rqLoading,
    error: rqError,
  } = useQuery({
    queryKey: queryKeys.keywords.tracking(`${effectiveKeyword}-${platform}-${currentPage}-${JSON.stringify(searchFilters)}`),
    queryFn: async () => {
      if (!effectiveKeyword) {
        return { games: [], page_details: null };
      }

      return measureOperation('fetchKeywordSearchResults', async () => {
        // Keyword Explorer keeps the old /tracker/keyword/{keyword} endpoint
        const response = await apiService.fetchKeywordSearchResults(effectiveKeyword, currentPage, platform, searchFilters, { legacy: true });
        if (isUpgradeResponse(response)) {
          setShowUpgradeModal(true);
          return { games: [], page_details: null };
        }
        return response;
      });
    },
    enabled: !!effectiveKeyword && !shouldUseSWR && effectiveKeyword.trim() !== '', // disable RQ while SWR polling is active and ensure non-empty keyword
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 20,
    retry: (failureCount, error) => {
      if (isUpgradeError(error)) {
        setShowUpgradeModal(true);
        return false;
      }
      return failureCount < 2;
    },
  })

  // On mount, restore last saved response (if any) so results, keyword and filters persist across route changes
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('keywordTrackingLastResponse')
      if (!raw) return
      const saved = JSON.parse(raw)
      if (!saved || !saved.keyword) return

      // Only restore when there is no effective keyword yet to avoid clobbering an active search
      const hasEffective = !!(effectiveKeyword && effectiveKeyword.trim())
      if (hasEffective) return

      const {
        keyword: kw,
        platform: savedPlatform,
        page,
        filters,
        responseData,
        isSearchMode: savedIsSearchMode,
        selectedChip: savedSelectedChip,
        submittedKeyword: savedSubmittedKeyword
      } = saved

      // Restore filters and platform
      setSearchFilters(filters || {})
      setActiveTab(savedPlatform || activeTab)
      setCurrentPage(page || 1)

      // Restore based on whether it was a chip selection or a search
      if (savedIsSearchMode === false && savedSelectedChip) {
        // Restore chip selection
        setSelectedChip(savedSelectedChip)
        setSearchValue(savedSelectedChip)
        setIsSearchMode(false)
        setHasSubmittedSearch(false)
        setSubmittedKeyword('')
      } else {
        // Restore search mode
        setSubmittedKeyword(savedSubmittedKeyword || kw)
        setSearchValue(savedSubmittedKeyword || kw)
        setHasSubmittedSearch(true)
        setIsSearchMode(true)
        setSelectedChip(null)
      }

      // Seed React Query cache with the saved response data so UI renders instantly
      const keyParam = `${kw}-${savedPlatform || ''}-${page || 1}-${JSON.stringify(filters || {})}`
      try {
        queryClient.setQueryData(queryKeys.keywords.tracking(keyParam), responseData)
      } catch (e) {
        // ignore
      }
    } catch (e) {
      // ignore parse errors
    }
  }, [effectiveKeyword, activeTab])

  // Extract search results and page details
  const responseData = shouldUseSWR ? swr.data : rqData
  // When there is no active keyword (no submitted search and no selected chip),
  // return empty results/page details to avoid showing stale data after clearing search.
  const hasActiveKeyword = !!(effectiveKeyword && effectiveKeyword.trim())
  const searchResults = useMemo(() => hasActiveKeyword ? (responseData?.games || []) : [], [hasActiveKeyword, responseData])
  const pageDetails = useMemo(() => hasActiveKeyword ? (responseData?.page_details || null) : null, [hasActiveKeyword, responseData])
  const totalPages = pageDetails?.total_pages || 1;

  // Persist latest response data to sessionStorage so results can be restored instantly
  useEffect(() => {
    try {
      if (effectiveKeyword && responseData && Object.keys(responseData).length > 0) {
        const payload = {
          keyword: effectiveKeyword,
          platform,
          page: currentPage,
          filters: searchFilters || {},
          responseData,
          savedAt: Date.now(),
          // Save mode information to distinguish between chip selection and search
          isSearchMode: isSearchMode,
          selectedChip: selectedChip, // Save chip selection if it exists
          submittedKeyword: isSearchMode ? submittedKeyword : null // Only save if it's a search
        }
        sessionStorage.setItem('keywordTrackingLastResponse', JSON.stringify(payload))
      }
    } catch (e) {
      // ignore storage errors
    }
  }, [effectiveKeyword, platform, currentPage, JSON.stringify(searchFilters || {}), responseData, isSearchMode, selectedChip, submittedKeyword])

  // Filter results based on active tab (client-side filtering, no API refetch)
  const filteredSearchResults = useMemo(() => {
    if (!searchResults || searchResults.length === 0) return [];

    // If the user applied an explicit platform filter via the FilterPanel,
    // use that as the active platform for client-side filtering.
    const filterPlatform = searchFilters?.platform
      ? (Array.isArray(searchFilters.platform) ? searchFilters.platform[0] : searchFilters.platform)
      : null;
    const effectivePlatform = filterPlatform || platform || activeTab;

    // If platform is already filtered by API (and no client-side override needed), return as-is
    if (platform && !filterPlatform) return searchResults;

    // Client-side filtering according to effective platform
    return searchResults.filter(result => result.platform === effectivePlatform);
  }, [searchResults, activeTab, platform, searchFilters]);

  // Handle tab change (no API refetch, just filter existing data)
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setCurrentPage(1); // Reset to first page when changing tabs
    trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_EXPLORER_PLATFORM_CHANGED, { platform: newTab, keyword: effectiveKeyword });
    // Note: No API refetch here, filtering is done client-side
  };

  // Handle chip selection (this will trigger new API call via React Query)
  const handleChipSelect = (chipLabel) => {
    setSelectedChip(chipLabel);
    setCurrentPage(1);
    setIsSearchMode(false);
    trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_SEARCH, { keyword: chipLabel, source: "suggested_chip" });

    // Force React Query to refetch to ensure fresh data with revenue placeholder
    // Invalidate all keyword tracking queries for this keyword to ensure fresh data
    try {
      // Invalidate queries that match the keyword pattern
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          // Match queries like ['keywords', 'tracking', 'keywordname-platform-page-filters']
          if (Array.isArray(queryKey) &&
            queryKey[0] === 'keywords' &&
            queryKey[1] === 'tracking' &&
            typeof queryKey[2] === 'string' &&
            queryKey[2].startsWith(`${chipLabel}-`)) {
            return true;
          }
          return false;
        },
        refetchActive: true,
        refetchInactive: true
      });
    } catch (err) {
      // No-op on invalidate errors
      console.warn('Error invalidating query on chip select', err)
    }
  };

  // Handle manual search
  const handleSearch = async (searchTerm, page = 1, filters = {}) => {
    if (!searchTerm.trim()) {
      setIsSearchMode(false);
      setSearchValue('');
      setHasSubmittedSearch(false);
      return;
    }

    // Ensure UI state reflects the submitted search. We set `submittedKeyword`
    // separately from `searchValue` to avoid triggering API calls while typing.
    const trimmed = searchTerm.trim();
    setSearchValue(searchTerm);
    setIsSearchMode(true);
    setSubmittedKeyword(trimmed);
    setHasSubmittedSearch(true); // Mark that user has submitted a search
    setCurrentPage(1); // Always reset to page 1 for new searches
    // store filters for subsequent requests
    setSearchFilters(filters || {});

    // If the applied filters include an explicit platform, switch the active tab
    // so the tab bar UI reflects the platform being searched.
    if (filters && filters.platform) {
      const filterPlatform = Array.isArray(filters.platform) ? filters.platform[0] : filters.platform;
      if (filterPlatform) {
        setActiveTab(filterPlatform);
      }
    }

    // If the user submits the same keyword again, React state setters may be a no-op
    // (React ignores setting identical state). That prevents React Query / SWR from
    // refetching because the query key stays the same. Force a refetch of both
    // systems so repeated submissions re-run the network request.
    try {
      // Determine the platform we'll use for the immediate refetch/invalidation.
      // Prefer an explicit platform passed in the filters (from header), otherwise
      // fall back to the current activeTab.
      const requestPlatform = (filters && filters.platform)
        ? (Array.isArray(filters.platform) ? filters.platform[0] : filters.platform)
        : activeTab;

      if (page === 1) {
        trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_SEARCH, { keyword: trimmed, platform: requestPlatform });
      }

      // Invalidate the React Query cache for this exact key so RQ will refetch
      const rqKey = queryKeys.keywords.tracking(`${trimmed}-${requestPlatform}-${page}-${JSON.stringify(filters || {})}`)
      queryClient.invalidateQueries({ queryKey: rqKey, refetchActive: true, refetchInactive: true })

      // Trigger SWR mutate for the keyword-search key (if SWR is in use it will revalidate)
      const swrKey = ['keyword-search', trimmed, page, requestPlatform, JSON.stringify(filters || {})]
      swrMutate(swrKey, undefined, { revalidate: true })
    } catch (err) {
      // No-op on mutate errors; network layer will surface errors via existing handlers
      // eslint-disable-next-line no-console
      console.warn('Error forcing search refetch', err)
    }
  };

  // Fully stop the active search/poll (e.g. when the user cancels the loading modal)
  // and leave the UI ready to accept a new request. Clears every piece of state that
  // feeds into `effectiveKeyword` - if any of these were left stale (in particular
  // `selectedChip`), cancelling a manual search would silently fall back to polling
  // whatever chip was last selected instead of actually stopping.
  const clearSearch = () => {
    setIsSearchMode(false);
    setHasSubmittedSearch(false);
    setSubmittedKeyword('');
    setSelectedChip(null);
    setActiveChip(-1);
    try { sessionStorage.removeItem('keywordTrackingLastResponse'); } catch (e) { /* ignore */ }
  };

  // Auto-run a search when `keyword` (and optionally `platform`) are present in the
  // URL query string, e.g. a link to /keyword-analysis?keyword=puzzle&platform=steam.
  // Re-runs whenever the query string changes (not just on mount) but only once per
  // distinct query string, and waits for platforms to load so the platform param can
  // be validated against the active platform ids.
  useEffect(() => {
    if (platformsLoading) return;
    if (processedUrlSearchRef.current === location.search) return;

    const params = new URLSearchParams(location.search);
    const keywordParam = params.get('keyword');
    if (!keywordParam || !keywordParam.trim()) return;

    processedUrlSearchRef.current = location.search;

    const platformParam = params.get('platform');
    const activePlatformIds = getActivePlatforms().map((p) => p.id);
    const filters = platformParam && activePlatformIds.includes(platformParam)
      ? { platform: platformParam }
      : {};

    handleSearch(keywordParam.trim(), 1, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformsLoading, location.search]);

  // Note: Removed automatic search mode exit on empty input to prevent interference with typing
  // Search mode will only be exited when user explicitly clears search or submits empty search

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_EXPLORER_PAGINATED, { page, keyword: effectiveKeyword, platform });
    // React Query will automatically refetch with new page
  };

  // No automatic chip selection - let user choose manually

  // Update active chip index when selected chip changes
  useEffect(() => {
    if (selectedChip) {
      const chipIndex = filterChips.findIndex(chip => chip.label === selectedChip);
      if (chipIndex !== -1) {
        setActiveChip(chipIndex);
      }
    } else {
      // When selectedChip is cleared (for example when search input is cleared),
      // reset the activeChip index so the Top Trending keyword visually deactivates
      setActiveChip(-1);
    }
  }, [selectedChip, filterChips]);

  // When search is cleared (hasSubmittedSearch === false), clear submittedKeyword
  useEffect(() => {
    if (!hasSubmittedSearch) {
      setSubmittedKeyword('')
    }
  }, [hasSubmittedSearch])

  // Fetch predefined keywords on mount
  useEffect(() => {
    refetchKeywords()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // When using SWR, backend may return an intermediate response { action: 'LOADING' }
  // to indicate the request is being processed. Expose a flag so UI can show
  // skeletons instead of a 'no results' message while processing.
  const isBackendProcessing = shouldUseSWR && !!swr?.data && swr?.data?.action === 'LOADING'

  // Unified loading flag for consumers
  const isLoadingCombined = keywordsLoading || (shouldUseSWR ? (swr.isLoading || swr.isValidating || isBackendProcessing) : rqLoading)

  const value = {
    // State
    activeTab,
    setActiveTab: handleTabChange, // Use optimized handler
    activeChip,
    setActiveChip,
    filterChips,
    searchValue,
    submittedKeyword,
    setSearchValue,
    searchResults: filteredSearchResults, // Use filtered results
    setSearchResults: () => { }, // Deprecated, managed by React Query
    filteredSearchResults,
    setFilteredSearchResults: () => { }, // Deprecated, managed by useMemo
    selectedChip,
    setSelectedChip: handleChipSelect, // Use optimized handler
    isLoading: isLoadingCombined,
    setIsLoading: () => { }, // Deprecated, managed by React Query
    isSearchMode,
    setIsSearchMode,
    isBackendProcessing,
    currentPage,
    setCurrentPage,
    totalPages,
    setTotalPages: () => { }, // Deprecated, managed by React Query
    pageDetails,
    setPageDetails: () => { }, // Deprecated, managed by React Query

    // Functions
    handleSearch,
    clearSearch,
    handlePageChange,
    refetchKeywords,
    setHasSubmittedSearch,
    searchFilters,
    setSearchFilters,

    // Keyword Explorer onboarding helpers
    showKeywordOnboarding,
    keywordOnboardingStep,
    setKeywordOnboardingStep,
    completeKeywordOnboarding: async () => {
      setShowKeywordOnboarding(false);
      setKeywordOnboardingStep(0);
      try {
        sessionStorage.setItem('keywordExplorerOnboardingSeen', 'true');
        sessionStorage.removeItem('keywordTutorialCurrentStep');
        window.dispatchEvent(new CustomEvent('keywordTutorialStep', { detail: { step: -1 } }));
        // Mark as completed when user clicks "Done"
        await markPageCompleted('keyword_explorer');
      } catch (e) {
        // ignore storage errors
        console.error('Error marking keyword_explorer as completed:', e);
      }
    },
    skipAllTutorials: async () => {
      setShowKeywordOnboarding(false);
      setKeywordOnboardingStep(0);
      try {
        sessionStorage.setItem('keywordExplorerOnboardingSeen', 'true');
        sessionStorage.setItem('appTrackingOnboardingSeen', 'true');
        sessionStorage.removeItem('keywordTutorialCurrentStep');
        window.dispatchEvent(new CustomEvent('keywordTutorialStep', { detail: { step: -1 } }));
        // Mark both keyword_explorer and app_tracking as completed
        await markPageCompleted('keyword_explorer', { app_tracking: true });
      } catch (e) {
        // ignore storage errors
      }
    },

    // Additional data
    hasData: filteredSearchResults.length > 0,
    isEmpty: !isLoadingCombined && filteredSearchResults.length === 0,
    isError: keywordsError || (shouldUseSWR ? swr.error : rqError),
    // Helper to open the shared upgrade modal from children
    openUpgradeModal: () => setShowUpgradeModal(true),
  };

  return (
    <OptimizedKeywordTrackingContext.Provider value={value}>
      {children}
      <AppsTrackingPlanModal
        show={showUpgradeModal}
        handleClose={() => setShowUpgradeModal(false)}
        title=" Upgrade your plan to unlock this feature"
      />
    </OptimizedKeywordTrackingContext.Provider>
  );
};

const OptimizedKeywordTrackingUIContainer = () => {
  // Set document title
  useEffect(() => {
    document.title = "Keyword Explorer | VR SEO Intelligence Tools Powered by AI & Big Data"
  }, []);

  return (
    <OptimizedKeywordTrackingProvider>
      <div className="innerLayout">
        <KeywordTrackingHeader />
        <OptimizedKeywordFilterTabs />
        <KeywordinteligenceCards />
        <KeywordSearchResults />
      </div>
    </OptimizedKeywordTrackingProvider>
  );
};

export default OptimizedKeywordTrackingUIContainer;
